// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Session conflict modal.
 * Shown when user tries to create/join a game but already has an active session.
 * Offers two choices: return to existing game or leave and join new.
 */

import { Button } from './Button';
import { Modal } from './Modal';
import type { ActiveSession } from '../types/game';

interface SessionConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingSession: ActiveSession;
  targetGameName: string;
  onReturnToExisting: () => void;
  onLeaveAndJoin: () => void;
  isLeaving?: boolean;
}

export function SessionConflictModal({
  isOpen,
  onClose,
  existingSession,
  targetGameName,
  onReturnToExisting,
  onLeaveAndJoin,
  isLeaving = false,
}: SessionConflictModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="You're Already in a Game">
      <div className="space-y-6">
        {/* Existing game info */}
        <div
          className="p-4 rounded-lg"
          style={{
            backgroundColor: 'var(--color-surface-dim)',
            border: 'var(--neo-border-medium)',
          }}
        >
          <p className="text-sm opacity-75 mb-2">Current game:</p>
          <p className="font-bold text-lg">
            {existingSession.game_id.replace(/-/g, ' ').replace(/_/g, ' ')}
          </p>
          <p className="font-mono text-sm mt-1 opacity-75">Room: {existingSession.room_code}</p>
        </div>

        <p className="text-center">
          You can only be in one game at a time.
          <br />
          Would you like to return to your current game or leave it to join{' '}
          <strong>{targetGameName || 'the new game'}</strong>?
        </p>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            variant="success"
            size="lg"
            className="flex-1"
            onClick={onReturnToExisting}
            disabled={isLeaving}
          >
            Return to Game
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className="flex-1"
            onClick={onLeaveAndJoin}
            disabled={isLeaving}
          >
            {isLeaving ? 'Leaving...' : 'Leave and Join New'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
